/**
 * RecentActivityTab — chronological feed surface for admin-review
 * (Block 6.F.5 §7).
 *
 * Consumes GET /v1/admin-review/timeline (UNION of change_log +
 * provider_notes + suggested_edits per design plan §3.2 from 6.F.4 r2,
 * with H1 SQL-boundary anonymity suppression per r2 §7.1).
 *
 * Filter chip "All / Mine" toggles ?actor_user_id between absent and
 * caller's own id (URL state via parent's setSearchParams). Pagination
 * via load-more button per §7.5 (NOT infinite scroll; NOT numbered).
 *
 * Archive/Restore confirms via ArchiveConfirmSheet; success calls
 * invalidateAdminReviewKeys to refresh feed + badge per §7.8.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@/lib/auth';
import { adminReviewApi, invalidateAdminReviewKeys } from '@/lib/adminReview';
import type { AdminTimelineResponse, AdminTimelineRow } from '@/lib/types';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';
import { RecentActivityRow } from './RecentActivityRow';
import { ArchiveConfirmSheet } from './ArchiveConfirmSheet';

const PAGE_SIZE = 50;

export interface RecentActivityTabProps {
  tenantSlug: string;
  /** Filter scope: 'all' (default) or 'mine'. URL-driven. */
  scope: 'all' | 'mine';
  onScopeChange: (next: 'all' | 'mine') => void;
  /** True for a seat holding archive_providers. */
  canArchive: boolean;
}

interface ArchiveDialogState {
  open: boolean;
  mode: 'archive' | 'restore';
  row: AdminTimelineRow | null;
}

export function RecentActivityTab({
  tenantSlug,
  scope,
  onScopeChange,
  canArchive,
}: RecentActivityTabProps): ReactNode {
  const { whoami } = useTenant();
  const queryClient = useQueryClient();
  const userId = whoami?.user_id ?? null;
  const actorFilter = scope === 'mine' && userId ? userId : null;

  const queryKey = useMemo(
    () => ['admin-review', 'timeline', tenantSlug, actorFilter] as const,
    [tenantSlug, actorFilter],
  );

  const query = useInfiniteQuery<AdminTimelineResponse, Error>({
    queryKey,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      adminReviewApi.timeline(
        tenantSlug,
        {
          actor_user_id: actorFilter ?? undefined,
          limit: PAGE_SIZE,
          offset: typeof pageParam === 'number' ? pageParam : 0,
        },
        signal,
      ),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more ? allPages.length * PAGE_SIZE : undefined,
    staleTime: 30_000,
  });

  // Local archive/restore state — set when overflow menu fires.
  const [archiveDialog, setArchiveDialog] = useState<ArchiveDialogState>({
    open: false,
    mode: 'archive',
    row: null,
  });
  const [toast, setToast] = useState<string | null>(null);

  const archiveMutation = useMutation({
    mutationFn: async (row: AdminTimelineRow) => {
      if (row.target_type === 'organization') {
        await adminReviewApi.archiveProvider(tenantSlug, row.target_id);
      } else if (row.target_type === 'individual') {
        await adminReviewApi.archiveIndividual(tenantSlug, row.target_id);
      } else {
        // Phase 1.6 — user_tenant rows have no archive affordance (user
        // lifecycle is deactivate via Team tab, not archive). Defensive
        // throw if a row reaches here despite the row-level overflow
        // hiding upstream.
        throw new Error(`archive not supported for target_type=${row.target_type}`);
      }
    },
    onSuccess: async (_, row) => {
      const name = row.target_display ?? 'resource';
      setToast(`Archived ${name}`);
      await invalidateAdminReviewKeys(queryClient);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: async (row: AdminTimelineRow) => {
      if (row.target_type === 'organization') {
        await adminReviewApi.restoreProvider(tenantSlug, row.target_id);
      } else if (row.target_type === 'individual') {
        await adminReviewApi.restoreIndividual(tenantSlug, row.target_id);
      } else {
        // Phase 1.6 — defensive (mirrors archiveMutation guard).
        throw new Error(`restore not supported for target_type=${row.target_type}`);
      }
    },
    onSuccess: async (_, row) => {
      const name = row.target_display ?? 'resource';
      setToast(`Restored ${name}`);
      await invalidateAdminReviewKeys(queryClient);
    },
  });

  // Auto-clear toast after 4 seconds.
  useEffect(() => {
    if (toast === null) return;
    const handle = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(handle);
  }, [toast]);

  const allRows: AdminTimelineRow[] =
    query.data?.pages.flatMap((p) => p.rows) ?? [];
  const hasNextPage = query.hasNextPage ?? false;
  const remaining =
    query.data?.pages.length && query.data.pages[query.data.pages.length - 1]
      ? Math.max(
          0,
          query.data.pages[query.data.pages.length - 1].total - allRows.length,
        )
      : 0;

  /**
   * Heuristic for "is target archived" — derived from the row's
   * direct_edit on is_active when present, defaulting to NOT archived.
   * The detail page is the source of truth; this is just for the
   * overflow-menu Archive/Restore wording.
   */
  function isTargetArchived(row: AdminTimelineRow): boolean {
    if (row.event_kind === 'direct_edit' && row.field_name === 'is_active') {
      return row.new_value === 'false';
    }
    return false;
  }

  return (
    <div>
      {/* Filter chips — All / Mine */}
      <div className="flex gap-2 mb-3 items-center">
        <span className="text-xs text-ink-soft">Show:</span>
        {(['all', 'mine'] as const).map((option) => {
          const isActive = scope === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => onScopeChange(option)}
              aria-pressed={isActive}
              className={
                'min-h-9 px-3 py-1 text-xs font-medium rounded-pill focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 ' +
                (isActive
                  ? 'bg-sage-100 text-sage-700 border border-sage-300'
                  : 'bg-paper text-ink-soft border border-sage-800/[8%] hover:border-sage-400/30')
              }
            >
              {option === 'all' ? 'All' : 'Mine'}
            </button>
          );
        })}
      </div>

      {/* Toast */}
      {toast && (
        <div
          role="status"
          className="mb-3 px-3 py-2 text-sm bg-sage-50 border border-sage-200 text-sage-800 rounded-md"
        >
          {toast}
        </div>
      )}

      {/* Loading */}
      {query.isLoading && (
        <div>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {/* Error */}
      {query.isError && (
        <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="recent activity" />
      )}

      {/* Empty */}
      {query.isSuccess && allRows.length === 0 && (
        <div className="px-3 py-8 text-center bg-sage-50 border border-sage-100 rounded-card text-sm text-ink">
          {scope === 'mine'
            ? "Quiet day. As you work, you'll see your activity here."
            : "Quiet around here. As the team works, you'll see their activity here."}
        </div>
      )}

      {/* Rows */}
      {query.isSuccess && allRows.length > 0 && (
        <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1">
          {allRows.map((row) => (
            <RecentActivityRow
              key={`${row.source}:${row.row_id}`}
              row={row}
              tenantSlug={tenantSlug}
              canArchive={canArchive}
              isTargetArchived={isTargetArchived(row)}
              onArchiveClick={(r) =>
                setArchiveDialog({ open: true, mode: 'archive', row: r })
              }
              onRestoreClick={(r) =>
                setArchiveDialog({ open: true, mode: 'restore', row: r })
              }
            />
          ))}
        </div>
      )}

      {/* Load-more / end-of-feed */}
      {query.isSuccess && allRows.length > 0 && (
        <div className="mt-3 text-center">
          {hasNextPage ? (
            <button
              type="button"
              onClick={() => query.fetchNextPage()}
              disabled={query.isFetchingNextPage}
              className="min-h-11 px-4 py-2 text-sm font-medium text-sage-700 border border-sage-300 rounded-md hover:bg-sage-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 disabled:opacity-50"
            >
              {query.isFetchingNextPage
                ? 'Loading…'
                : remaining > 0
                  ? `Load more (${remaining} remaining)`
                  : 'Load more'}
            </button>
          ) : (
            <span className="text-xs text-ink-soft">End of activity feed.</span>
          )}
        </div>
      )}

      {/* Archive / Restore confirm */}
      {archiveDialog.row !== null && (
        <ArchiveConfirmSheet
          isOpen={archiveDialog.open}
          mode={archiveDialog.mode}
          targetName={archiveDialog.row.target_display ?? 'this resource'}
          onClose={() =>
            setArchiveDialog({ open: false, mode: archiveDialog.mode, row: null })
          }
          onConfirm={async () => {
            const r = archiveDialog.row;
            if (!r) return;
            if (archiveDialog.mode === 'archive') {
              await archiveMutation.mutateAsync(r);
            } else {
              await restoreMutation.mutateAsync(r);
            }
          }}
        />
      )}
    </div>
  );
}
